import queryString from 'query-string';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PagedResultState } from '../../common/CommonResult';
import { API } from '../../api';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
} from 'antd';
import dayjs from 'dayjs';
import CustomerSelect from '../../lib/CustomerSelect';
import { notifyError, priceFormat } from '../../store/utils';
import { DownloadOutlined } from '@ant-design/icons';
import DeliveryManSelect from '../../lib/DeliveryManSelect';
import { ParcelStatusContext } from '../../context/ParcelStatusProvider';

export const ShippingReport = () => {
  const { statusList } = useContext(ParcelStatusContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [channels, updateChannels] = useState([]);

  const [state, changeState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const [downloadLoading, setDownloadLoading] = useState(false);

  const fetchChannelTypes = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/settings/channels`);
      const list = result || [];
      updateChannels(list);
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  useEffect(() => {
    if (location.search) {
      let params = queryString.parse(location.search);
      form.setFieldsValue({
        ...params,
        orderDate:
          params?.startDate && params.endDate
            ? [dayjs(params?.startDate as string), dayjs(params.endDate as string)]
            : [null, null],
        scanDate:
          params?.scanStartDate && params.scanEndDate
            ? [dayjs(params?.scanStartDate as string), dayjs(params.scanEndDate as string)]
            : [null, null],
        customerNo: params?.customerNo ? parseInt('' + params.customerNo) : undefined,
        status: params?.status || undefined,
        svc: params?.svc || undefined,
        deliveryManNo: params?.deliveryManNo || undefined,
      });
      fetchContent(params);
    } else {
      navigate(`?page=1`);
    }
  }, [location.search, navigate]);

  useEffect(() => {
    fetchChannelTypes();
  }, []);

  const fetchContent = async (filter: any) => {
    try {
      const payload = {
        ...filter,
        sortName: 'createdAt',
        sort: 'DESC',
      };
      changeState({ loading: true, result: {} });
      const response = await API.get({ apiVersion: 'api' })(
        `/report?${queryString.stringify(payload)}`
      );
      changeState({ loading: false, result: response.result });
    } catch (e) {
      changeState({ loading: false, error: true, result: {}, errors: e });
    }
  };
  const handleTableChange = (pagination: TablePaginationConfig) => {
    const params = queryString.parse(location.search);
    const search = {
      ...params,
      page: pagination.current || 1,
      size: pagination.pageSize,
    };
    navigate(`/report/shipping?${queryString.stringify(search)}`);
  };

  const download = useCallback(async () => {
    try {
      setDownloadLoading(true);

      const response = await API.get({
        apiVersion: 'api',
        responseType: 'blob',
      })(`/report/download${location.search}&sortName=createdAt&sort=desc`);
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'data.xlsx');
      document.body.appendChild(link);
      link.click();
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setDownloadLoading(false);
    }
  }, [location]);

  const columns = [
    {
      title: '№',
      dataIndex: 'no',
      width: 40,
    },
    {
      title: 'Баримт',
      dataIndex: 'barcode',
      width: 70,
    },
    {
      title: 'Ав.Огноо',
      dataIndex: 'createdAt',
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
      width: 100,
    },
    {
      title: 'Илгээгч',
      dataIndex: 'order',
      render: (value: any) => value?.customer?.name,
      width: 70,
    },
    {
      title: 'Авсан хүргэгч',
      dataIndex: 'scanMember',
      render: (value: any) => value?.firstName,
    },
    {
      title: 'Хүргэх хаяг',
      dataIndex: 'order',
      render: (value: any) => value?.receiverAddressDetail,
    },
    {
      title: 'ХҮ. хүргэгч',
      dataIndex: 'order',
      render: (value: any) => value?.lastDeliveryMan?.firstName,
    },
    {
      title: 'Хэмжээ',
      dataIndex: 'weightType',
      render: (value: any) => value?.name,
    },
    {
      title: 'Дүн',
      dataIndex: 'price',
      // render: (value: any) => value?.customer?.name,
    },
    {
      title: 'Скан төлөв',
      dataIndex: 'parcelStatus',
      render: (value: any) => value?.name,
    },
    {
      title: 'Скан огноо',
      dataIndex: 'scanDate',
      render: (value: any) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const onSearch = (value: any) => {
    const params = queryString.parse(location.search);
    const search = {
      ...params,
      ...value,
      page: 1,
    };
    navigate(`/report/shipping?${queryString.stringify(search)}`);
    console.log(value);
  };

  const channelOptions = useMemo(() => {
    return channels.map((item: any) => {
      return {
        label: item.text,
        value: item.code,
      };
    });
  }, [channels]);

  const statusOptions = useMemo(() => {
    return statusList.map((item: any) => {
      return {
        label: item.name,
        value: item.cd,
      };
    });
  }, [statusList]);
  return (
    <Card
      title={`Урьдчилсан захиалгын илгээмж - ${state.result?.total ?? 0}`}
      extra={
        <Button loading={downloadLoading} onClick={() => download()} icon={<DownloadOutlined />}>
          Татах
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <Col span={4}>
            <Form.Item label="Ав.Огноо" name="orderDate">
              <DatePicker.RangePicker
                onChange={(value) => {
                  onSearch({
                    startDate: value?.[0] ? value[0].startOf('day').toISOString() : '',
                    endDate: value?.[1] ? value[1].endOf('day').toISOString() : '',
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Скан огноо" name="scanDate">
              <DatePicker.RangePicker
                onChange={(value) => {
                  onSearch({
                    scanStartDate: value?.[0] ? value[0].startOf('day').toISOString() : '',
                    scanEndDate: value?.[1] ? value[1].endOf('day').toISOString() : '',
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Харилцагч" name={'customerNo'}>
              <CustomerSelect showSearch allowClear onChange={(e) => onSearch({ customerNo: e })} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Төлөв" name={'status'}>
              <Select
                placeholder="Бүгд"
                options={statusOptions}
                allowClear
                onChange={(e) => onSearch({ status: e })}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Хүргэгч" name={'deliveryManNo'}>
              <DeliveryManSelect
                allowClear
                showSearch
                onChange={(e) => onSearch({ deliveryManNo: e })}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Суваг" name={'svc'}>
              <Select
                placeholder="Бүгд"
                options={channelOptions}
                allowClear
                onChange={(e) => onSearch({ svc: e })}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Flex style={{ marginBottom: 24 }}>
        <div>
          Нийт тоо: <b>{state?.result?.total}ш</b> Нийт дүн:{' '}
          <b>{priceFormat(state?.result?.amount || 0)}₮</b>
        </div>
      </Flex>
      <Table
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
          position: ['bottomRight', 'topRight'],
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        loading={state.loading}
        dataSource={state.result.data}
        columns={columns}
        rowKey="no"
      />
    </Card>
  );
};
